import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DashboardUserPlatformModeSettings,
  NotificationCategory,
  NotificationType,
  dashboardUserGetDashboardUserPlatformModeSettingsOptions,
  dashboardUserGetDashboardUserPlatformModeSettingsQueryKey,
  dashboardUserUpdateDashboardUserPlatformModeSettingsMutation,
} from '@column/api';
import { Button } from '@column/column-ui-kit';
import { Checkbox } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { Loading, Section } from '~/components';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface NotificationCategoryConfig {
  category: NotificationCategory;
  description: string;
  label: string;
}

interface NotificationGroup {
  categories: NotificationCategoryConfig[];
  heading: string;
}

const NOTIFICATION_CATEGORY_CONFIGS: Record<NotificationCategory, NotificationCategoryConfig> = {
  [NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL]: {
    category: NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL,
    label: 'Pending Approval',
    description: 'A transfer is waiting for approval before it can be sent.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_INITIATED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_INITIATED,
    label: 'Initiated',
    description: 'A transfer has been created.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_COMPLETED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_COMPLETED,
    label: 'Completed',
    description: 'A transfer has completed.',
  },
  [NotificationCategory.TRANSFER_OUTGOING_RETURNED]: {
    category: NotificationCategory.TRANSFER_OUTGOING_RETURNED,
    label: 'Returned',
    description: 'A transfer has been returned.',
  },
  [NotificationCategory.TRANSFER_INCOMING_RECEIVED]: {
    category: NotificationCategory.TRANSFER_INCOMING_RECEIVED,
    label: 'Received',
    description: 'A transfer has been received.',
  },
  [NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN]: {
    category: NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN,
    label: 'Pending Return',
    description: 'A transfer may be returned.',
  },
  [NotificationCategory.TRANSFER_INCOMING_RETURNED]: {
    category: NotificationCategory.TRANSFER_INCOMING_RETURNED,
    label: 'Returned',
    description: 'A transfer has been returned.',
  },
};

const NOTIFICATION_GROUPS: NotificationGroup[] = [
  {
    heading: 'Outgoing Transfers',
    categories: [
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_PENDING_APPROVAL],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_INITIATED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_COMPLETED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_OUTGOING_RETURNED],
    ],
  },
  {
    heading: 'Incoming Transfers',
    categories: [
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_RECEIVED],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_PENDING_RETURN],
      NOTIFICATION_CATEGORY_CONFIGS[NotificationCategory.TRANSFER_INCOMING_RETURNED],
    ],
  },
];
const NOTIFICATION_CATEGORIES = Object.values(NOTIFICATION_CATEGORY_CONFIGS);
const SAVE_ACTION_EXIT_DURATION_MS = 150;

type NotificationSettingsByCategory = Record<NotificationCategory, boolean>;

const getDefaultNotificationSettings = (): NotificationSettingsByCategory =>
  NOTIFICATION_CATEGORIES.reduce((acc, { category }) => {
    acc[category] = false;
    return acc;
  }, {} as NotificationSettingsByCategory);

const getNotificationSettingsByCategory = (
  settings?: DashboardUserPlatformModeSettings
): NotificationSettingsByCategory => {
  const values = getDefaultNotificationSettings();
  const categories = settings?.notifications?.categories ?? {};

  NOTIFICATION_CATEGORIES.forEach(({ category }) => {
    values[category] = categories[category]?.[NotificationType.EMAIL]?.enabled ?? false;
  });

  return values;
};

const getNotificationSettingsRequest = (
  settingsByCategory: NotificationSettingsByCategory
): DashboardUserPlatformModeSettings => ({
  notifications: {
    categories: Object.fromEntries(
      NOTIFICATION_CATEGORIES.map(({ category }) => [
        category,
        {
          [NotificationType.EMAIL]: {
            enabled: settingsByCategory[category],
          },
        },
      ])
    ),
  },
});

const serializeNotificationSettings = (settingsByCategory: NotificationSettingsByCategory): string =>
  JSON.stringify(settingsByCategory);

export const PageNotifications: React.FC = () => {
  const { currentPlatform, currentUser } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId;

  const platformModeSettingsQuery = useQuery({
    ...dashboardUserGetDashboardUserPlatformModeSettingsOptions(),
    enabled: !!currentUser && !!platformId,
  });
  const updatePlatformModeSettings = useMutation(dashboardUserUpdateDashboardUserPlatformModeSettingsMutation());
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettingsByCategory>(getDefaultNotificationSettings);
  const [savedNotificationSettings, setSavedNotificationSettings] = useState<NotificationSettingsByCategory | null>(
    null
  );
  const [isSaveActionExiting, setIsSaveActionExiting] = useState(false);
  const saveActionExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSaveActionExitTimeout = useCallback(() => {
    if (saveActionExitTimeoutRef.current) {
      clearTimeout(saveActionExitTimeoutRef.current);
      saveActionExitTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearSaveActionExitTimeout, [clearSaveActionExitTimeout]);

  useEffect(() => {
    const nextSettings = getNotificationSettingsByCategory(platformModeSettingsQuery.data);
    setNotificationSettings(nextSettings);
    setSavedNotificationSettings(nextSettings);
  }, [platformModeSettingsQuery.data]);

  const hasUnsavedChanges = useMemo(() => {
    if (!savedNotificationSettings) {
      return false;
    }

    return (
      serializeNotificationSettings(notificationSettings) !== serializeNotificationSettings(savedNotificationSettings)
    );
  }, [notificationSettings, savedNotificationSettings]);

  const handleNotificationChange = useCallback(
    (category: NotificationCategory, enabled: boolean) => {
      clearSaveActionExitTimeout();
      setIsSaveActionExiting(false);
      setNotificationSettings((currentSettings) => ({
        ...currentSettings,
        [category]: enabled,
      }));
    },
    [clearSaveActionExitTimeout]
  );

  const isNotificationInputDisabled = updatePlatformModeSettings.isPending || !platformId;

  const handleSave = useCallback(async () => {
    try {
      await updatePlatformModeSettings.mutateAsync({
        body: getNotificationSettingsRequest(notificationSettings),
      });
      clearSaveActionExitTimeout();
      setIsSaveActionExiting(true);
      setSavedNotificationSettings(notificationSettings);
      saveActionExitTimeoutRef.current = setTimeout(() => {
        setIsSaveActionExiting(false);
        saveActionExitTimeoutRef.current = null;
      }, SAVE_ACTION_EXIT_DURATION_MS);
      await queryClient.invalidateQueries({
        queryKey: dashboardUserGetDashboardUserPlatformModeSettingsQueryKey(),
      });
      addSuccessNotification({
        content: 'Notification settings saved',
      });
    } catch (error) {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to save notification settings',
      });
    }
  }, [
    addDangerNotification,
    addSuccessNotification,
    clearSaveActionExitTimeout,
    notificationSettings,
    queryClient,
    updatePlatformModeSettings,
  ]);

  const shouldShowSaveAction = hasUnsavedChanges || isSaveActionExiting;

  return (
    <>
      {shouldShowSaveAction && (
        <PageActions>
          <div
            className={`transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
              isSaveActionExiting
                ? 'pointer-events-none translate-x-2 scale-95 opacity-0'
                : 'translate-x-0 scale-100 opacity-100'
            }`}
          >
            <Button
              isDisabled={!platformId || updatePlatformModeSettings.isPending || isSaveActionExiting}
              isLoading={updatePlatformModeSettings.isPending}
              onClick={handleSave}
              size="small"
            >
              Save changes
            </Button>
          </div>
        </PageActions>
      )}
      <Loading isLoading={platformModeSettingsQuery.isLoading}>
        <Section headline="Email Notifications" description="Configure which notifications will be sent to your email.">
          {NOTIFICATION_GROUPS.map(({ heading, categories }) => (
            <div className="-mt-px grid first:mt-0" key={heading}>
              <div className="border border-border bg-muted/40 px-3 py-2 text-xs font-semibold uppercase leading-4 text-muted-foreground">
                {heading}
              </div>
              <div className="grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))] border-l border-border">
                {categories.map(({ category, description, label }) => (
                  <div
                    aria-disabled={isNotificationInputDisabled}
                    aria-pressed={notificationSettings[category]}
                    className="flex min-h-[72px] cursor-pointer items-start gap-3 border-b border-r border-border p-3 text-left transition-colors hover:bg-muted/40 aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:bg-transparent"
                    key={category}
                    onClick={
                      isNotificationInputDisabled
                        ? undefined
                        : () => handleNotificationChange(category, !notificationSettings[category])
                    }
                    role="button"
                    tabIndex={isNotificationInputDisabled ? -1 : 0}
                  >
                    <Checkbox
                      checked={notificationSettings[category]}
                      className="mt-0.5 shrink-0"
                      disabled={isNotificationInputDisabled}
                      onCheckedChange={
                        isNotificationInputDisabled
                          ? undefined
                          : (enabled) => handleNotificationChange(category, enabled === true)
                      }
                      onClick={(event) => event.stopPropagation()}
                    />
                    <div className="grid min-w-0 gap-0.5">
                      <span className="text-sm leading-5 text-foreground">{label}</span>
                      <span className="text-xs leading-4 text-muted-foreground">{description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>
      </Loading>
    </>
  );
};
